import { VuexModule, Module, Mutation, Action } from 'vuex-module-decorators';

import vuex from '..';
import Utils from '@/modules/Utils';
import API, { Types } from '../../modules/API';
import cloneDeep from 'lodash/cloneDeep';
import router from '../../router';
import store from 'store';
import { EventType, IncidentType } from '@/modules/Types';
import { isAnyAllowedRoles } from '@/modules/constants';

export interface Project {
  id: string;
  name: string;
  company: string;
  description: string;
  secret: string;
  logo: any;
  created: Date;
  modified: Date;
  users: number[];
  restricted: string[];
  // notificationStreamId: string;
}

export interface ProjectMetadata {
  id: string;
  power: object[];
  screw: object[];
  painted: object[];
  activity: object[];
  pole: object[];
  totalDevices: number;
}

export interface CabinetAlertMetric {
  grade: number;
  top_priority: number;
}

export const fetchInstallationsLastMonth = async (companyId: string, projectId: string) => {
  try {
    const response = await API.dashboardAPI(
      `v2/companies/${companyId}/projects/${projectId}/installations_last_month`,
      'GET',
      {},
      null
    );

    if (!response) {
      console.warn('No installations data received');
      return null;
    }

    return response;
  } catch (e) {
    console.error('[fetchInstallationsLastMonth] Error:', e);
    throw e;
  }
};

const fetchIncidents = async ({
  fields = [
    "id",
    "device_id",
    "user_id",
    "cabinet_id",
    "phase",
    "circuit_number",
    "pole_number",
    "priority",
    "state",
    "value",
    "probability",
    "socket_type",
    "incident_type",
    "created",
    "modified",
    "managed_area"
  ],
  filters = {},
  page = 0,
  limit = 1000,
  order_by = "-created"
}: {
  fields?: string[];
  filters?: Record<string, any>;
  page?: number;
  limit?: number;
  order_by?: string;
}) => {
  const project_data = (vuex.state as any).User.project;

  if (!project_data || project_data.id === 'allProjects') {
    throw new Error('Invalid project data');
  }

  const backend_url = `v2/companies/${project_data.company}/projects/${project_data.id}/incidents/filter/`;
  // console.log('[fetchIncidents] Calling backend incidents/filter API:', backend_url);

  try {
    let allIncidents: any[] = [];
    let currentPage = page;
    let totalCount = 0;
    let hasMorePages = true;

    while (hasMorePages) {
      const response = await API.backendServicesAPI(backend_url, 'POST', {}, {
        fields,
        page: currentPage,
        limit,
        filters,
        order_by
      });

      // console.log(`[fetchIncidents] Page ${currentPage} response:`, response);

      if (response && response.result && response.result.length > 0) {
        const fetchedCount = response.result.length;
        allIncidents = allIncidents.concat(response.result);
        totalCount = response.total_count || 0;

        // console.log(`[fetchIncidents] Fetched ${allIncidents.length} of ${totalCount} incidents (page returned ${fetchedCount} items)`);

        // Stop if we have all incidents OR if the last page returned fewer items than the limit
        if (allIncidents.length >= totalCount || fetchedCount < limit) {
          hasMorePages = false;
        } else {
          currentPage++;
        }
      } else {
        hasMorePages = false;
      }
    }

    // console.log(`[fetchIncidents] Completed fetching all ${allIncidents.length} incidents`);

    return {
      result: allIncidents,
      total_count: totalCount
    };
  } catch (e) {
    console.error('[fetchIncidents] Error fetching incidents:', e);
    throw e;
  }
};

const calculateCabinetMetrics = (incidents: any[]): Map<string, CabinetAlertMetric> => {
  const metricsMap = new Map<string, CabinetAlertMetric>();

  // Group incidents by cabinet_id
  const incidentsByCabinet = new Map<string, any[]>();

  incidents.forEach((incident) => {
    const cabinetId = incident.cabinet_id;
    if (!cabinetId) return;

    if (!incidentsByCabinet.has(cabinetId)) {
      incidentsByCabinet.set(cabinetId, []);
    }
    incidentsByCabinet.get(cabinetId)!.push(incident);
  });

  const priorityIdGrade = { 1: 1, 2: 100, 3: 1000 };

  // Calculate metrics for each cabinet
  incidentsByCabinet.forEach((incidentList, cabinetId) => {
    const priorityIdCount = { 1: 0, 2: 0, 3: 0 };

    incidentList.forEach((incident) => {
      if (incident.priority === 0) return; // Skip info events

      if (incident.priority >= 1 && incident.priority <= 3) {
        priorityIdCount[incident.priority]++;
      }
    });

    let grade = 0;
    let top_priority = 0;

    for (const priorityId in priorityIdCount) {
      const count = priorityIdCount[priorityId];
      grade += count * priorityIdGrade[priorityId];

      if (Number(priorityId) > top_priority && count > 0) {
        top_priority = Number(priorityId);
      }
    }

    metricsMap.set(cabinetId, { grade, top_priority });
  });

  return metricsMap;
};

// let devicesDB, cabinetsDB;
@Module({ namespaced: true })
export default class Projects extends VuexModule {
  list: Project[] = [];
  projects_count: number = 0;
  alertsPriorityMetricsByCabinet: Map<string, CabinetAlertMetric> = new Map();
  projectAlerts: any[] = [];
  projectEvents: any[] = [];
  isLoadingAlerts: boolean = false;
  isLoadingEvents: boolean = false;
  lastAlertsProjectId: string = '';
  lastEventsProjectId: string = '';
  eventTypes: EventType[] = [];
  incidentTypes: IncidentType[] = [];
  isLoadingEventTypes: boolean = false;
  isLoadingIncidentTypes: boolean = false;
  // alerts: {
  //   [projectId: string]: {
  //     cabinets?: {
  //       [cabinetId: string]: any
  //     },
  //     general_list?: Array<{
  //       type?: string,
  //       incident_type?: string,
  //       title?: string,
  //       priority_id?: number,
  //       updated_at?: string
  //     }>
  //   }
  // } = {};
  // events: object = {};
  images: object = {};
  projects_metadata: any = new Map();
  projects_total_devices: Record<string, number> = {};
  isLoadingProjectSummary: boolean = true;
  // projects_users: any = new Map();

  get alertsByPriority() {
    let high_priority = 0;
    let medium_priority = 0;
    let low_priority = 0;
    this.alertsPriorityMetricsByCabinet.forEach((cabinet_metric: CabinetAlertMetric) => {
      if (cabinet_metric.top_priority >= 1 && cabinet_metric.top_priority <= 3) {
        if (cabinet_metric.top_priority === 1) low_priority++;
        else if (cabinet_metric.top_priority === 2) medium_priority++;
        else high_priority++;
      }
    });
    return {
      high: high_priority,
      medium: medium_priority,
      low: low_priority
    };
  }

  get cabinetsWithActionableAlertsCount() {
    let count = 0;
    this.alertsPriorityMetricsByCabinet.forEach((cabinet_metric: CabinetAlertMetric) => {
      if (cabinet_metric.top_priority >= 1 && cabinet_metric.top_priority <= 3) {
        count++;
      }
    });
    return count;
  }

  @Action
  async load() {
    vuex.commit('Projects/flush');
    vuex.commit('User/setIsLoading', true);

    const user = store.get('user');
    try {
      const res = await API.get(Types.COMPANIES, `${user.company}/${Types.PROJECTS}`, {});

      // Optimized filtering logic for Cerbos and non-Cerbos users
      if (res && res.results && user) {
        const projectsList = res.results;
        let filteredProjects = [];

        // Check if user is Cerbos user (XX role)
        if (user.role === 'XX') {
          // For Cerbos users: check permissions first, then forbidden list only if needed
          const permissionsChecks = await Promise.all(
            projectsList.map(async (project) => {
              try {
                const response = await API.post(
                  '',
                  `companies/${project.company}/users/${user.id}/permissions/`,
                  {
                    project: project.id
                  }
                );
                if (response && response.roles) {
                  // Store user permissions for future use
                  project.roles = response.roles;
                  
                  const hasValidRoles = isAnyAllowedRoles(response.roles);
                  
                  if (!hasValidRoles) {
                    // User has invalid roles, filter out this project
                    project.restricted = []; // Set empty for consistency
                    return false;
                  }
                  
                  // User has valid roles, now check forbidden users list
                  const restricted = await API.get(
                    Types.COMPANIES,
                    `${project.company}/projects/${project.id}/properties/`,
                    { name: 'dashboard.Forbidden_users' }
                  );
                  
                  project.restricted = restricted && restricted.results && restricted.results[0] && restricted.results[0].value
                    ? restricted.results[0].value
                    : [];
                  
                  // Filter out if user is in forbidden list
                  if (project.restricted.includes(user.username)) {
                    return false;
                  }
                  
                  return true; // User has valid roles and not in forbidden list
                }
              } catch (error) {
                console.error('[Projects.load] Error checking Cerbos permissions:', error);
                project.restricted = [];
                project.roles = [];
              }
              return false;
            })
          );
          
          filteredProjects = projectsList.filter((_, index) => permissionsChecks[index]);
        } else {
          // For non-Cerbos users: fetch forbidden users for all projects
          const projectsWithRestricted = await Promise.all(
            projectsList.map(async (project) => {
              try {
                const restricted = await API.get(
                  Types.COMPANIES,
                  `${project.company}/projects/${project.id}/properties/`,
                  { name: 'dashboard.Forbidden_users' }
                );
                
                project.restricted = restricted && restricted.results && restricted.results[0] && restricted.results[0].value
                  ? restricted.results[0].value
                  : [];
                
                // Set empty roles for non-Cerbos users
                project.roles = [];
                
                return project;
              } catch (error) {
                console.error('[Projects.load] Error fetching forbidden users:', error);
                project.restricted = [];
                project.roles = [];
                return project;
              }
            })
          );

          // Filter out projects where user is in forbidden list
          filteredProjects = projectsWithRestricted.filter((project: any) => {
            return !(project.restricted && Array.isArray(project.restricted) && project.restricted.includes(user.username));
          });
        }

        vuex.commit('Projects/setProjectsCount', filteredProjects.length);

        // Add filtered projects to list
        await Promise.all(
          filteredProjects.map((data: any) => {
            vuex.dispatch('Projects/add', data);
            return vuex.dispatch('Projects/loadImage', {
              project_id: data.id,
              company_id: data.company
            });
          })
        );
      }
    } catch (error) {
      console.error('[Projects.load] Error loading projects:', error);
    }

    const current_project = (vuex.state as any).User.project;
    const projects_list = (vuex.state as any).Projects.list;

    // User has no access to this project
    if (projects_list && projects_list.length && current_project && projects_list.findIndex((project) => project.id === current_project.id) === -1) {
      vuex.commit('User/unsetProject');
      router.push('/');
    }
  }

  @Action({ commit: 'add' }) create(data) {
    if (!data || !data.name) return;
    return API.post(Types.PROJECTS, '', data)
      .then((res) => {
        vuex.dispatch('Global/throwNotify', {
          type: 'success',
          title: 'Success!',
          text: `Project successfully created`
        });

        return res;
      })
      .catch(() => {
        vuex.dispatch('Global/throwNotify', {
          type: 'error',
          title: 'Error!',
          text: `Project did not created`
        });
      });
  }

  @Mutation flush() {
    this.list = [];
    this.projects_count = -1;
  }

  @Mutation
  setProjectsList(project) {
    this.list.push(project);
    vuex.commit('Projects/sortProjectsList');
  }

  @Mutation
  setProjectsCount(length) {
    this.projects_count = length;
  }

  @Mutation
  updateProjectData(project_metadata) {
    const { id, ...data } = project_metadata;
    this.projects_metadata.set(id, cloneDeep(data));
  }

  @Mutation
  clearProjectsData() {
    this.projects_metadata = new Map();
  }

  @Mutation
  setProjectsTotalDevices(data: any) {
    this.projects_total_devices = data;
  }

  @Mutation
  setIsLoadingProjectSummary(isLoading: boolean) {
    this.isLoadingProjectSummary = isLoading;
  }



  @Action
  async fetchProjectSummary({ company_id, project_id }: { company_id: string; project_id: string }) {
    if (this.isLoadingProjectSummary) {
      return;
    }
    try {
      const response = await API.dashboardAPI(
        `v2/companies/${company_id}/projects/${project_id}/project_summary`,
        'GET',
        {},
        {}
      );
      vuex.commit('Projects/updateProjectData', { id: project_id, ...response });
    } catch (e) {
      console.error('[fetchProjectSummary] Error:', e);
    }
  }

  @Action
  async loadAllProjectsMetadata(projects: any[]) {
    try {
      vuex.commit('Projects/setIsLoadingProjectSummary', true);
      // console.log('[loadAllProjectsMetadata] Loading metadata for', projects.length, 'projects');

      // Load metadata for each project that doesn't have it yet
      for (const project of projects) {
        const hasMetadata = (vuex.state as any).Projects.projects_metadata.has(project.id);

        if (!hasMetadata) {
          // console.log('[loadAllProjectsMetadata] Loading metadata for project:', project.id);
          await vuex.dispatch('Projects/loadSelectedProjectMetadata', {
            company_id: project.company,
            project_id: project.id
          });
        }
      }

      // console.log('[loadAllProjectsMetadata] Completed loading metadata for all projects');
    } catch (e) {
      console.error('[loadAllProjectsMetadata] Error:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingProjectSummary', false);
    }
  }


  @Action
  async loadSelectedProjectMetadata({ company_id, project_id }: { company_id: string; project_id: string }) {
    try {
      vuex.commit('Projects/setIsLoadingProjectSummary', true);

      // Fetch project summary from API
      await vuex.dispatch('Projects/fetchProjectSummary', {
        company_id,
        project_id
      });

      // Get project summary from state
      const projectSummary = (vuex.state as any).Projects.projects_metadata.get(project_id);

      if (!projectSummary) {
        console.error('[loadSelectedProjectMetadata] No project summary in state');
        return;
      }

      const project_metadata = {
        id: project_id,
        ...projectSummary
      };


      // Commit the metadata to store
      vuex.commit('Projects/updateProjectData', project_metadata);
    } catch (e) {
      console.error('[loadSelectedProjectMetadata] Error:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingProjectSummary', false);
    }
  }

  @Mutation
  sortProjectsList() {
    this.list.sort((project1, project2) =>
      project1.name.localeCompare(project2.name)
    );
  }

  @Mutation
  updateRestrictedByProject(property) {
    const project = this.list.find((project) => project.id === property.project_id);
    if (!project) return;

    project.restricted = property.value;
  }

  @Action async add(data: Project) {
    if (!data || !data.id || Utils.find((vuex.state as any).Projects.list, data.id) !== -1) return;
    // API.get(Types.PROJECTS, `${data.id}/streams/`).then((res) => {
    //   if (res && res.results) {
    //     const notificationStream = res.results.find(
    //       (s) => s.name === 'dashboard_notifications'
    //     );
    //     if (notificationStream) {
    //       data.notificationStreamId = notificationStream.id;
    //     } else {
    //       data.notificationStreamId = '';
    //     }
    //   } else {
    //     data.notificationStreamId = '';
    //   }
    // });
    data.created = data.created ? new Date(data.created) : null;
    data.modified = data.modified ? new Date(data.modified) : null;

    // data.restricted = [];

    vuex.commit('Projects/setProjectsList', { ...data });
  }

  @Action async getAlerts() {
    if (this.isLoadingAlerts) {
      // console.log('[getAlerts] Already loading alerts, skipping...');
      return;
    }

    const project_data = (vuex.state as any).User.project;

    if (!project_data || project_data.id === 'allProjects') return;

    if (this.lastAlertsProjectId === project_data.id) {
      // console.log('[getAlerts] Alerts already loaded for project:', project_data.id);
      // Still check if we need to fetch incident types
      if (this.projectAlerts.length > 0) {
        vuex.dispatch('Projects/fetchIncidentTypes');
      }
      return;
    }

    // console.log('[getAlerts] Starting to load alerts for project:', project_data.id);
    vuex.commit('Projects/setIsLoadingAlerts', true);


    try {
      const response_backend = await fetchIncidents({
        filters: {
          state: "!!close"
        }
      });
      // console.log('[getAlerts] Backend response:', response_backend);
      vuex.commit('Projects/setProjectAlerts', response_backend);
      vuex.commit('Projects/setLastAlertsProjectId', project_data.id);

      // Fetch incident types if there are alerts and types haven't been fetched yet
      if (response_backend && response_backend.result && response_backend.result.length > 0) {
        vuex.dispatch('Projects/fetchIncidentTypes');
      }
    } catch (e) {
      console.error('[getAlerts] Error fetching alerts:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingAlerts', false);
    }
  }


  @Mutation setProjectAlerts(alerts) {
    this.projectAlerts = alerts.result || [];
    // If alerts is a flat array of incidents, calculate metrics directly
    this.alertsPriorityMetricsByCabinet = calculateCabinetMetrics(alerts.result || []);
  }

  @Mutation setProjectEvents(events) {
    this.projectEvents = events.result || [];
  }

  @Mutation setIsLoadingAlerts(isLoading: boolean) {
    this.isLoadingAlerts = isLoading;
  }

  @Mutation setIsLoadingEvents(isLoading: boolean) {
    this.isLoadingEvents = isLoading;
  }

  @Mutation setLastAlertsProjectId(projectId: string) {
    this.lastAlertsProjectId = projectId;
  }

  @Mutation setLastEventsProjectId(projectId: string) {
    this.lastEventsProjectId = projectId;
  }

  @Mutation clearAlertsCache() {
    this.lastAlertsProjectId = '';
    this.projectAlerts = [];
    this.alertsPriorityMetricsByCabinet = new Map();
  }

  @Mutation clearEventsCache() {
    this.lastEventsProjectId = '';
    this.projectEvents = [];
  }

  @Mutation removeIncidentFromList({ incidentId, type }: { incidentId: string; type: 'alerts' | 'events' }) {
    if (type === 'alerts') {
      this.projectAlerts = this.projectAlerts.filter((incident) => incident.id.toString() !== incidentId.toString());
      this.alertsPriorityMetricsByCabinet = calculateCabinetMetrics(this.projectAlerts);
    } else {
      this.projectEvents = this.projectEvents.filter((incident) => incident.id.toString() !== incidentId.toString());
    }
  }

  @Action async getEvents() {
    if (this.isLoadingEvents) {
      // console.log('[getEvents] Already loading events, skipping...');
      return;
    }

    const project_data = (vuex.state as any).User.project;
    if (!project_data || project_data.id === 'allProjects') return;

    if (this.lastEventsProjectId === project_data.id) {
      // console.log('[getEvents] Events already loaded for project:', project_data.id);
      // Still check if we need to fetch event types
      if (this.projectEvents.length > 0) {
        vuex.dispatch('Projects/fetchEventTypes');
      }
      return;
    }

    // console.log('[getEvents] Starting to load events for project:', project_data.id);
    vuex.commit('Projects/setIsLoadingEvents', true);

    try {
      const backend_url = `v2/companies/${project_data.company}/projects/${project_data.id}/events/?created_last_days=7`;
      const response = await API.backendServicesAPI(backend_url, 'GET', {}, {});

      // Format response to match expected structure
      const response_backend = {
        result: response || [],
        count: response ? response.length : 0
      };

      vuex.commit('Projects/setProjectEvents', response_backend);
      vuex.commit('Projects/setLastEventsProjectId', project_data.id);

      // Fetch event types if there are events and types haven't been fetched yet
      if (response_backend && response_backend.result && response_backend.result.length > 0) {
        vuex.dispatch('Projects/fetchEventTypes');
      }
    } catch (e) {
      console.error('[getEvents] Error fetching events:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingEvents', false);
    }
  }

  @Action
  async loadImage(project_data) {
    const image = {
      [project_data.project_id]: {
        url: '@/assets/images/tondo_icons/default_header_logo.svg',
        color: 'rgba(0, 0, 0, 0.05)'
      }
    };

    const logoProperty = await API.get(
      Types.COMPANIES,
      `${project_data.company_id}/projects/${project_data.project_id}/properties/`,
      { name: 'meta.logo' }
    );

    const meta_logo =
      logoProperty &&
        logoProperty.results &&
        logoProperty.results[0].value &&
        logoProperty.results[0].value
        ? logoProperty.results[0].value
        : '';

    if (meta_logo['url']) image[project_data.project_id].url = meta_logo['url'];
    if (meta_logo['color'])
      image[project_data.project_id].color = meta_logo['color'];

    vuex.commit('Projects/addImage', image);
  }

  @Mutation
  addImage(image) {
    this.images = { ...this.images, ...image };
  }

  @Mutation
  changeImage(data) {
    this.images[data[0]] = data[1];
  }

  @Mutation
  setEventTypes(eventTypes: EventType[]) {
    this.eventTypes = eventTypes;
  }

  @Mutation
  setIncidentTypes(incidentTypes: IncidentType[]) {
    this.incidentTypes = incidentTypes;
  }

  @Mutation
  setIsLoadingEventTypes(isLoading: boolean) {
    this.isLoadingEventTypes = isLoading;
  }

  @Mutation
  setIsLoadingIncidentTypes(isLoading: boolean) {
    this.isLoadingIncidentTypes = isLoading;
  }

  @Mutation
  clearProjectsState() {
    this.projectAlerts = [];
    this.projectEvents = [];
    this.eventTypes = [];
    this.incidentTypes = [];
    this.lastAlertsProjectId = '';
    this.lastEventsProjectId = '';
    this.alertsPriorityMetricsByCabinet = new Map();
    this.isLoadingAlerts = false;
    this.isLoadingEvents = false;
    this.isLoadingEventTypes = false;
    this.isLoadingIncidentTypes = false;
  }

  @Action
  async fetchEventTypes() {
    const isLoadingEventTypes = (vuex.state as any).Projects.isLoadingEventTypes;
    const eventTypesLength = (vuex.state as any).Projects.eventTypes.length;
    if (isLoadingEventTypes || eventTypesLength > 0) {
      return;
    }

    try {
      vuex.commit('Projects/setIsLoadingEventTypes', true);
      const response = await API.backendServicesAPI('v2/event_types/', 'GET', {}, {});

      if (response && Array.isArray(response)) {
        vuex.commit('Projects/setEventTypes', response);
      }
    } catch (e) {
      console.error('[fetchEventTypes] Error fetching event types:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingEventTypes', false);
    }
  }

  @Action
  async fetchIncidentTypes() {
    const isLoadingIncidentTypes = (vuex.state as any).Projects.isLoadingIncidentTypes;
    const incidentTypesLength = (vuex.state as any).Projects.incidentTypes.length;

    if (isLoadingIncidentTypes || incidentTypesLength > 0) {
      return;
    }

    try {
      vuex.commit('Projects/setIsLoadingIncidentTypes', true);
      const response = await API.backendServicesAPI('v2/incident_types/', 'GET', {}, {});

      if (response && Array.isArray(response)) {
        vuex.commit('Projects/setIncidentTypes', response);
      }
    } catch (e) {
      console.error('[fetchIncidentTypes] Error fetching incident types:', e);
    } finally {
      vuex.commit('Projects/setIsLoadingIncidentTypes', false);
    }
  }

  @Action
  async fetchAllIncidentAndEventTypes() {
    await Promise.all([
      vuex.dispatch('Projects/fetchEventTypes'),
      vuex.dispatch('Projects/fetchIncidentTypes')
    ]);
  }
}
